
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './src/context/AuthContext';
import { CaseFileStyleProvider, useCaseFileStyle } from './src/context/CaseFileStyleContext';
import { CaseFileStyleToggle } from './src/components/casefile/CaseFileStyleToggle';
import OfficialsPage from './OfficialsPage';
import HomePage from './HomePage';
import OfficialDetailPage from './OfficialDetailPage';
import AdminPage from './AdminPage';
import AboutPage from './AboutPage';
import OfficeHubPage, { OFFICE_HUBS } from './OfficeHubPage';
import LoginPage from './src/LoginPage';
import RegisterPage from './src/RegisterPage';
import OAuthCallbackPage from './src/OAuthCallbackPage';
import MyAdoptionsPage from './src/MyAdoptionsPage';
import NotificationsPage from './src/NotificationsPage';
import VerifyEmailPage from './src/VerifyEmailPage';
import ForgotPasswordPage from './src/ForgotPasswordPage';
import ResetPasswordPage from './src/ResetPasswordPage';
import DashboardPage from './src/DashboardPage';
import { featureFlags } from './featureFlags';

// ---------------------------------------------------------------------------
// AAC Shield Logo (inline SVG, matches favicon)
// ---------------------------------------------------------------------------

function AACLogo({ size = 40 }: { size?: number }) {
  return (
    <svg viewBox="0 0 512 512" width={size} height={size} className="shrink-0">
      <path d="M256 28 L460 110 L460 280 Q460 420 256 490 Q52 420 52 280 L52 110 Z" fill="#1B3A5C" stroke="#C5A55A" strokeWidth="18"/>
      <path d="M256 50 L440 125 L440 275 Q440 405 256 472 L256 50Z" fill="#162F4A" opacity="0.4"/>
      <text x="256" y="215" textAnchor="middle" fill="white" fontSize="130" fontWeight="900" fontFamily="Inter,Arial,sans-serif" letterSpacing="-3">AAC</text>
      <circle cx="230" cy="340" r="52" fill="none" stroke="#C5A55A" strokeWidth="14"/>
      <line x1="270" y1="380" x2="325" y2="435" stroke="#C5A55A" strokeWidth="14" strokeLinecap="round"/>
      <circle cx="215" cy="325" r="16" fill="#C5A55A" opacity="0.25"/>
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Theme Toggle
// ---------------------------------------------------------------------------

function useTheme() {
  const [dark, setDark] = useState(() => {
    if (typeof window === 'undefined') return false;
    const saved = localStorage.getItem('aac_theme');
    if (saved) return saved === 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark);
    localStorage.setItem('aac_theme', dark ? 'dark' : 'light');
  }, [dark]);

  return { dark, toggle: () => setDark(d => !d) };
}

function ThemeToggle({ dark, toggle }: { dark: boolean; toggle: () => void }) {
  return (
    <button
      onClick={toggle}
      className="p-2.5 sm:p-2 rounded-lg text-navy-400 dark:text-cream-300 hover:bg-cream-200 dark:hover:bg-navy-600 active:bg-cream-300 dark:active:bg-navy-500 transition min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center"
      aria-label={dark ? "Switch to light mode" : "Switch to dark mode"}
    >
      {dark ? (
        <svg className="w-5 h-5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
      ) : (
        <svg className="w-5 h-5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" /></svg>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// User Menu
// ---------------------------------------------------------------------------

function UserMenu() {
  const { user, isAuthenticated, logout, token } = useAuth();
  const [showMenu, setShowMenu] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!isAuthenticated || !token) return;
    const fetchUnread = async () => {
      try {
        const resp = await fetch('/api/notifications/stats', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (resp.ok) {
          const data = await resp.json();
          setUnreadCount(data.unread ?? 0);
        }
      } catch { /* non-critical */ }
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 120000);
    return () => clearInterval(interval);
  }, [isAuthenticated, token]);

  if (!isAuthenticated) {
    return (
      <div className="flex flex-col md:flex-row items-stretch md:items-center gap-2 md:gap-2">
        <Link
          to="/login"
          className="text-navy-700 dark:text-cream-300 hover:text-gold-600 dark:hover:text-gold-400 active:text-gold-700 dark:active:text-gold-500 transition text-xs sm:text-[11px] font-black uppercase tracking-widest px-4 py-3 md:px-0 md:py-0 text-center md:text-left hover:bg-cream-100 md:hover:bg-transparent dark:hover:bg-navy-700 md:dark:hover:bg-transparent rounded-lg md:rounded-none min-h-[48px] md:min-h-0 flex items-center justify-center"
        >
          Login
        </Link>
        <Link
          to="/register"
          className="bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white px-4 py-3 md:px-3 md:py-1.5 rounded-lg text-xs sm:text-[11px] font-black uppercase tracking-widest transition min-h-[48px] md:min-h-0 flex items-center justify-center"
        >
          Register
        </Link>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <Link
        to="/notifications"
        className="relative p-2 rounded-lg hover:bg-cream-200 dark:hover:bg-navy-600 transition text-navy-400 dark:text-cream-300"
        aria-label="Notifications"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center bg-gold-500 text-navy-900 text-[9px] font-black rounded-full">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </Link>
      <div className="relative">
      <button
        onClick={() => setShowMenu(!showMenu)}
        className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-cream-200 dark:hover:bg-navy-600 transition"
      >
        {user?.avatarUrl ? (
          <img
            src={user.avatarUrl}
            alt={user.displayName || user.username}
            className="w-8 h-8 rounded-full"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-sm">
            {(user?.displayName || user?.username || 'U')[0].toUpperCase()}
          </div>
        )}
        <svg
          className={`w-4 h-4 text-navy-400 dark:text-cream-300 transition ${showMenu ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {showMenu && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setShowMenu(false)}
          />
          <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-navy-800 rounded-lg shadow-xl border border-navy-200 dark:border-navy-700 z-20">
            <div className="p-3 border-b border-navy-200 dark:border-navy-700">
              <p className="font-semibold text-navy-900 dark:text-cream-50">
                {user?.displayName || user?.username}
              </p>
              <p className="text-xs text-navy-500 dark:text-cream-400">
                {user?.email || '@' + user?.username}
              </p>
            </div>
            <div className="py-1">
              <Link
                to="/dashboard"
                onClick={() => setShowMenu(false)}
                className="block px-4 py-2 text-sm text-navy-700 dark:text-cream-200 hover:bg-cream-100 dark:hover:bg-navy-700 transition"
              >
                Dashboard
              </Link>
              <Link
                to="/my-adoptions"
                onClick={() => setShowMenu(false)}
                className="block px-4 py-2 text-sm text-navy-700 dark:text-cream-200 hover:bg-cream-100 dark:hover:bg-navy-700 transition"
              >
                My Adoptions
              </Link>
              <Link
                to="/notifications"
                onClick={() => setShowMenu(false)}
                className="block px-4 py-2 text-sm text-navy-700 dark:text-cream-200 hover:bg-cream-100 dark:hover:bg-navy-700 transition"
              >
                Notifications
              </Link>
              {user?.role === 'admin' && (
                <Link
                  to="/admin"
                  onClick={() => setShowMenu(false)}
                  className="block px-4 py-2 text-sm text-navy-700 dark:text-cream-200 hover:bg-cream-100 dark:hover:bg-navy-700 transition"
                >
                  Admin Panel
                </Link>
              )}
            </div>
            <div className="border-t border-navy-200 dark:border-navy-700 py-1">
              <button
                onClick={() => {
                  logout();
                  setShowMenu(false);
                }}
                className="block w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-cream-100 dark:hover:bg-navy-700 transition"
              >
                Logout
              </button>
            </div>
          </div>
        </>
      )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Token Handler (for OAuth callback)
// ---------------------------------------------------------------------------

function TokenHandler() {
  const { setToken } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');

    if (token) {
      setToken(token);
      // Remove token from URL
      const newUrl = location.pathname + (location.hash || '');
      navigate(newUrl, { replace: true });
    }
  }, [location, setToken, navigate]);

  return null;
}

// ---------------------------------------------------------------------------
// App Content (inside AuthProvider)
// ---------------------------------------------------------------------------

function AppContent() {
  const { dark, toggle } = useTheme();
  const { isAuthenticated } = useAuth();
  const { style: caseFileStyle } = useCaseFileStyle();
  const isEvidenceBoard = caseFileStyle === 'evidence-board';
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (mobileMenuOpen && !target.closest('.mobile-menu') && !target.closest('.mobile-menu-button')) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [mobileMenuOpen]);

  return (
    <div className="min-h-screen flex flex-col">
      <TokenHandler />
      <header
        className={`sticky top-0 z-50 ${
          isEvidenceBoard
            ? 'bg-navy-900 border-b-[3px] border-gold-500'
            : 'glass-panel border-b-2 border-navy-900 dark:border-gold-500'
        }`}
      >
        <div className="container mx-auto px-3 sm:px-4 h-14 sm:h-16 flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-2 sm:gap-2.5 shrink-0 group">
            <AACLogo size={32} />
            <div className="flex flex-col">
              <span className={`text-xs sm:text-sm font-black tracking-tight uppercase leading-none ${isEvidenceBoard ? 'text-cream-50' : 'text-navy-900 dark:text-cream-50'}`}>Adopt-A-Coward</span>
              <span className={`text-[8px] sm:text-[9px] font-bold uppercase tracking-[0.12em] sm:tracking-[0.15em] leading-none mt-0.5 hidden xs:block ${isEvidenceBoard ? 'text-navy-300' : 'text-navy-400 dark:text-navy-300'}`}>
                {isEvidenceBoard ? 'Civic Accountability' : 'Form AAC-1 · Case Registry'}
              </span>
            </div>
          </Link>

          {/* Desktop Navigation - Hidden on mobile */}
          <div className="hidden md:flex flex-col items-center justify-center">
            <img
              src="https://meidasnews.com/.image/cs_srgb%2Cq_auto:eco/MTk5NDU2MTUxOTk2MjEyNjM4/mtn__96x40.svg"
              alt="MeidasTouch Network"
              className={`h-6 transition ${isEvidenceBoard ? 'opacity-50 hover:opacity-90 brightness-0 invert' : 'opacity-40 hover:opacity-80'}`}
            />
            <span className={`text-[7px] font-bold uppercase tracking-widest mt-0.5 ${isEvidenceBoard ? 'text-navy-300' : 'text-navy-400 dark:text-navy-300'}`}>Inspired by a MeidasTouch Episode</span>
          </div>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-4 shrink-0">
            <nav className="flex items-center space-x-5 text-[11px] font-black uppercase tracking-widest">
              <Link to="/" className={isEvidenceBoard ? 'text-cream-100 hover:text-gold-400 transition' : 'text-navy-500 dark:text-cream-300 hover:text-gold-600 dark:hover:text-gold-400 active:text-gold-700 dark:active:text-gold-500 transition'}>Home</Link>
              <Link to="/officials" className={isEvidenceBoard ? 'text-cream-100 hover:text-gold-400 transition' : 'text-navy-500 dark:text-cream-300 hover:text-gold-600 dark:hover:text-gold-400 active:text-gold-700 dark:active:text-gold-500 transition'}>Officials</Link>
              <Link to="/governors" className={isEvidenceBoard ? 'text-cream-100 hover:text-gold-400 transition' : 'text-navy-500 dark:text-cream-300 hover:text-gold-600 dark:hover:text-gold-400 active:text-gold-700 dark:active:text-gold-500 transition'}>Governors</Link>
              <Link to="/about" className={isEvidenceBoard ? 'text-cream-100 hover:text-gold-400 transition' : 'text-navy-500 dark:text-cream-300 hover:text-gold-600 dark:hover:text-gold-400 active:text-gold-700 dark:active:text-gold-500 transition'}>About</Link>
            </nav>
            <CaseFileStyleToggle compact />
            <ThemeToggle dark={dark} toggle={toggle} />
            <UserMenu />
          </div>

          {/* Mobile Actions */}
          <div className="flex md:hidden items-center gap-1.5">
            <div className="hidden xs:block"><CaseFileStyleToggle compact /></div>
            <ThemeToggle dark={dark} toggle={toggle} />
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`mobile-menu-button p-2 rounded-lg transition min-w-[44px] min-h-[44px] flex items-center justify-center ${
                isEvidenceBoard
                  ? 'text-cream-100 hover:bg-navy-800 active:bg-navy-700'
                  : 'text-navy-500 dark:text-cream-300 hover:bg-cream-200 dark:hover:bg-navy-700 active:bg-cream-300 dark:active:bg-navy-600'
              }`}
              aria-label="Toggle menu"
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="mobile-menu md:hidden border-t border-cream-200 dark:border-navy-700 bg-white dark:bg-navy-800 shadow-lg">
            <nav className="container mx-auto px-3 py-3 flex flex-col space-y-1">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-3 text-sm font-bold uppercase tracking-wide text-navy-700 dark:text-cream-200 hover:bg-cream-100 dark:hover:bg-navy-700 active:bg-cream-200 dark:active:bg-navy-600 rounded-lg transition min-h-[48px] flex items-center"
              >
                Home
              </Link>
              <Link
                to="/officials"
                onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-3 text-sm font-bold uppercase tracking-wide text-navy-700 dark:text-cream-200 hover:bg-cream-100 dark:hover:bg-navy-700 active:bg-cream-200 dark:active:bg-navy-600 rounded-lg transition min-h-[48px] flex items-center"
              >
                Officials
              </Link>
              <Link
                to="/governors"
                onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-3 text-sm font-bold uppercase tracking-wide text-navy-700 dark:text-cream-200 hover:bg-cream-100 dark:hover:bg-navy-700 active:bg-cream-200 dark:active:bg-navy-600 rounded-lg transition min-h-[48px] flex items-center"
              >
                Governors
              </Link>
              <Link
                to="/about"
                onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-3 text-sm font-bold uppercase tracking-wide text-navy-700 dark:text-cream-200 hover:bg-cream-100 dark:hover:bg-navy-700 active:bg-cream-200 dark:active:bg-navy-600 rounded-lg transition min-h-[48px] flex items-center"
              >
                About
              </Link>
              <div className="border-t border-cream-200 dark:border-navy-700 my-2"></div>
              <UserMenu />
              <div className="pt-2 px-4">
                <img
                  src="https://meidasnews.com/.image/cs_srgb%2Cq_auto:eco/MTk5NDU2MTUxOTk2MjEyNjM4/mtn__96x40.svg"
                  alt="MeidasTouch Network"
                  className="h-6 opacity-40"
                />
                <span className="text-[9px] font-bold text-navy-400 dark:text-navy-300 uppercase tracking-widest mt-1 block">Inspired by a MeidasTouch Episode</span>
              </div>
            </nav>
          </div>
        )}
      </header>
      <main className="flex-grow">
        <Routes>
          <Route
            path="/"
            element={
              featureFlags.watchlistHomeV2 && isAuthenticated ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <HomePage />
              )
            }
          />
          <Route path="/officials" element={<OfficialsPage />} />
          <Route path="/officials/:id" element={<OfficialDetailPage />} />
          <Route path="/about" element={<AboutPage />} />
          {OFFICE_HUBS.map((hub) => (
            <Route key={hub.slug} path={`/${hub.slug}`} element={<OfficeHubPage config={hub} />} />
          ))}
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/oauth/callback" element={<OAuthCallbackPage />} />
          <Route path="/my-adoptions" element={<MyAdoptionsPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
        </Routes>
      </main>
    </div>
  );
}

// --- App Root ---

export default function App() {
  return (
    <Router>
      <AuthProvider>
        <CaseFileStyleProvider>
          <AppContent />
        </CaseFileStyleProvider>
      </AuthProvider>
    </Router>
  );
}
